import {
  Fragment,
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
  type SyntheticEvent,
} from "react";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import { BaseButton, BaseTooltip, type BaseAppearance } from "../base";
import { useMonkeysComponentAttributes } from "../../provider/context";
import { getOverlaySurfaceClassName } from "../primitives/internal/overlaySurface";
export { AppShellSidebar } from "./AppShellSidebar";
export type {
  AppShellSidebarClassNames,
  AppShellSidebarProps,
} from "./AppShellSidebar";
export { UserAccountMenu } from "./UserAccountMenu";
export type {
  UserAccountMenuClassNames,
  UserAccountMenuItem,
  UserAccountMenuProps,
  UserAccountMenuRadius,
  UserAccountMenuSelectorConfig,
  UserAccountMenuSelectorOption,
  UserAccountMenuSide,
  UserAccountMenuTriggerVariant,
  UserAccountMenuUser,
} from "./UserAccountMenu";

// ============================================================
// cn utility (inline clsx + tailwind-merge–lite for zero deps)
// ============================================================
export function cn(...inputs: (string | undefined | null | false)[]): string {
  return inputs.filter(Boolean).join(" ");
}

// ============================================================
// NavButton
// ============================================================
export interface NavButtonProps {
  /** 是否处于激活状态 */
  active?: boolean;
  /** 是否显示但禁止交互 */
  disabled?: boolean;
  /** 左侧图标 */
  icon?: ReactNode;
  /** 右侧附加内容（如 chevron） */
  postfix?: ReactNode;
  /** 点击回调 */
  onClick?: () => void;
  className?: string;
  children: ReactNode;
}

export function NavButton({
  active,
  disabled,
  icon,
  postfix,
  onClick,
  className,
  children,
}: NavButtonProps) {
  return (
    <button
      type="button"
      aria-disabled={disabled}
      aria-pressed={active}
      disabled={disabled}
      className={cn(
        "flex w-full cursor-pointer select-none items-center gap-2 rounded-xl border p-2 text-left text-xs text-[hsl(var(--foreground))] transition-colors",
        active
          ? "border-[hsl(var(--input))] bg-[hsl(var(--muted))] font-bold"
          : disabled
            ? "border-transparent"
            : "border-transparent hover:border-[hsl(var(--input))]/60 hover:bg-[hsl(var(--muted))]/60",
        disabled && "!cursor-not-allowed opacity-60",
        className,
      )}
      onClick={onClick}
    >
      <div className="w-[20px] [&_svg]:h-[16px] [&_svg]:w-[16px]">{icon}</div>
      <span className="text-sm">{children}</span>
      {postfix}
    </button>
  );
}

// ============================================================
// AppHeader
// ============================================================
export interface HeaderTab {
  id: string;
  label: string;
}

export interface AppHeaderLogo {
  /** Logo 图片 URL，若为空则显示 fallback */
  url?: string;
  /** 品牌名称 */
  title: string;
  /** 点击 Logo 回调 */
  onClick?: () => void;
}

export interface AppHeaderUser {
  name?: string;
  email?: string;
  photo?: string;
}

export interface AppHeaderProps {
  /** 品牌 Logo */
  logo: AppHeaderLogo;
  /** 导航 tabs */
  tabs?: HeaderTab[];
  /** 当前激活的 tab id */
  activeTab?: string;
  /** tab 切换回调 */
  onTabChange?: (tabId: string) => void;
  /** 用户信息 */
  user?: AppHeaderUser;
  /** 退出登录回调 */
  onLogout?: () => void;
  /** 退出登录文案 */
  logoutLabel?: string;
  /** 用户菜单中的额外菜单项（在退出登录按钮之前渲染） */
  userMenuItems?: ReactNode;
  /** header 右侧额外内容 */
  extra?: ReactNode;
  className?: string;
}

export function AppHeader({
  logo,
  tabs,
  activeTab,
  onTabChange,
  user,
  onLogout,
  logoutLabel = "退出登录",
  userMenuItems,
  extra,
  className,
}: AppHeaderProps) {
  const getUserInitials = (name: string) =>
    name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);

  return (
    <header
      className={cn(
        "flex h-[72px] w-full shrink-0 items-center justify-between gap-4 rounded-xl border border-[hsl(var(--input))] bg-[hsl(var(--background))] p-[var(--global-spacing,0.75rem)]",
        className,
      )}
    >
      {/* Logo */}
      <div
        className="flex shrink-0 cursor-pointer items-center gap-2"
        onClick={logo.onClick}
      >
        <div
          className={cn(
            "flex h-8 w-8 items-center justify-center rounded-lg overflow-hidden",
            logo.url
              ? "bg-transparent"
              : "bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))]",
          )}
        >
          {logo.url ? (
            <img
              src={logo.url}
              alt={logo.title}
              className="h-full w-full object-contain"
            />
          ) : (
            <span className="text-sm font-bold">{logo.title.charAt(0)}</span>
          )}
        </div>
        <span className="text-lg font-semibold whitespace-nowrap">
          {logo.title}
        </span>
      </div>

      {/* Separator + Tabs */}
      {tabs && tabs.length > 0 && (
        <>
          <div className="h-1/2 w-px bg-[hsl(var(--border))]" />
          <div className="flex flex-1 items-center">
            <div className="inline-flex h-9 items-center gap-1 rounded-md bg-transparent p-1">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  className={cn(
                    "inline-flex items-center justify-center whitespace-nowrap rounded-sm px-3 py-1.5 text-sm font-medium transition-all",
                    tab.id === activeTab
                      ? "bg-[hsl(var(--muted))] text-[hsl(var(--foreground))] shadow-sm"
                      : "text-[hsl(var(--muted-foreground))] hover:bg-[hsl(var(--muted))]/50",
                  )}
                  onClick={() => onTabChange?.(tab.id)}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          </div>
        </>
      )}
      {(!tabs || tabs.length === 0) && <div className="flex-1" />}

      {/* Right side */}
      <div className="flex shrink-0 items-center gap-4">
        {extra}
        {user && (
          <DropdownMenu.Root>
            <DropdownMenu.Trigger asChild>
              <button className="flex h-8 w-8 items-center justify-center rounded-full hover:ring-2 hover:ring-[hsl(var(--ring))]/20 transition-all overflow-hidden focus-visible:outline-none">
                {user.photo ? (
                  <img
                    src={user.photo}
                    alt={user.name}
                    className="h-8 w-8 rounded-full object-cover"
                  />
                ) : (
                  <div className="flex h-8 w-8 items-center justify-center rounded-full bg-[hsl(var(--muted))] text-xs font-medium">
                    {user.name ? getUserInitials(user.name) : "U"}
                  </div>
                )}
              </button>
            </DropdownMenu.Trigger>
            <DropdownMenu.Portal>
              <DropdownMenu.Content
                className="z-50 w-52 rounded-md border border-[hsl(var(--border))] bg-[hsl(var(--popover))] p-1 text-[hsl(var(--popover-foreground))] shadow-md animate-in fade-in-0 zoom-in-95"
                sideOffset={5}
                align="end"
              >
                {/* User info label */}
                <DropdownMenu.Label className="flex items-center gap-2 px-2 py-1.5 font-normal">
                  {user.photo ? (
                    <img
                      src={user.photo}
                      alt={user.name}
                      className="h-8 w-8 rounded-full object-cover"
                    />
                  ) : (
                    <div className="flex h-8 w-8 items-center justify-center rounded-full bg-[hsl(var(--muted))] text-xs font-medium">
                      {user.name ? getUserInitials(user.name) : "U"}
                    </div>
                  )}
                  <div className="flex flex-col">
                    <span className="text-sm font-medium leading-none">
                      {user.name}
                    </span>
                    {user.email && (
                      <span className="text-xs text-[hsl(var(--muted-foreground))] leading-none mt-1">
                        {user.email}
                      </span>
                    )}
                  </div>
                </DropdownMenu.Label>

                <DropdownMenu.Separator className="h-px bg-[hsl(var(--border))] my-1" />

                <DropdownMenu.Group>
                  {userMenuItems}

                  {/* Logout */}
                  <DropdownMenu.Item
                    className="relative flex cursor-pointer select-none items-center gap-2 rounded-sm px-2 py-1.5 text-sm text-[hsl(var(--destructive))] outline-none transition-colors hover:bg-[hsl(var(--accent))]"
                    onSelect={() => onLogout?.()}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                      <polyline points="16 17 21 12 16 7" />
                      <line x1="21" y1="12" x2="9" y2="12" />
                    </svg>
                    {logoutLabel}
                  </DropdownMenu.Item>
                </DropdownMenu.Group>
              </DropdownMenu.Content>
            </DropdownMenu.Portal>
          </DropdownMenu.Root>
        )}
      </div>
    </header>
  );
}

// ============================================================
// AppSidebar
// ============================================================
export interface SidebarNavItem {
  id: string;
  label: string;
  icon?: ReactNode;
  disabled?: boolean;
  /** 如果有 children 则为分组，点击展开/折叠 */
  children?: SidebarNavItem[];
}

export interface SidebarAccount {
  name?: string;
  photo?: string;
}

export interface AppSidebarProps {
  /** Logo */
  logo?: AppHeaderLogo;
  /** 导航菜单 */
  navItems: SidebarNavItem[];
  /** 当前路径/激活的 item id */
  activeItemId?: string;
  /** 点击导航项回调 */
  onNavigate?: (itemId: string) => void;
  /** 账户信息 */
  account?: SidebarAccount;
  /** 退出登录回调 */
  onLogout?: () => void;
  /** 退出登录文案 */
  logoutLabel?: string;
  /** 底部额外内容（如版本号） */
  footer?: ReactNode;
  /** 默认展开的分组 id 列表 */
  defaultExpandedGroups?: string[];
  className?: string;
}

export function AppSidebar({
  logo,
  navItems,
  activeItemId,
  onNavigate,
  account,
  onLogout,
  logoutLabel = "退出登录",
  footer,
  defaultExpandedGroups,
  className,
}: AppSidebarProps) {
  return (
    <div
      className={cn(
        "flex w-[13.7rem] shrink-0 flex-col justify-between gap-[var(--global-spacing,0.75rem)] overflow-y-hidden rounded-xl border border-[hsl(var(--input))] bg-[hsl(var(--background))] p-5",
        className,
      )}
    >
      {/* Logo */}
      {logo && (
        <div
          className="flex shrink-0 cursor-pointer items-center gap-2"
          onClick={logo.onClick}
        >
          <div
            className={cn(
              "flex h-8 w-8 items-center justify-center rounded-lg overflow-hidden",
              logo.url
                ? "bg-transparent"
                : "bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))]",
            )}
          >
            {logo.url ? (
              <img
                src={logo.url}
                alt={logo.title}
                className="h-full w-full object-contain"
              />
            ) : (
              <span className="text-sm font-bold">{logo.title.charAt(0)}</span>
            )}
          </div>
          <span className="text-lg font-semibold whitespace-nowrap">
            {logo.title}
          </span>
        </div>
      )}

      {/* Navigation */}
      <SidebarNavList
        items={navItems}
        activeItemId={activeItemId}
        onNavigate={onNavigate}
        defaultExpandedGroups={defaultExpandedGroups}
      />

      {/* Account Section */}
      {(account || footer) && (
        <div className="flex flex-col gap-2">
          {account && (
            <div className="flex w-full flex-col gap-2 rounded-lg bg-[hsl(var(--muted))] p-2 shadow-sm">
              <div className="flex w-full items-center justify-between px-3 py-1">
                <div className="flex items-center gap-2">
                  {account.photo ? (
                    <img
                      src={account.photo}
                      alt={account.name}
                      className="h-5 w-5 rounded-full object-cover"
                    />
                  ) : (
                    <div className="flex h-5 w-5 items-center justify-center rounded-full bg-[hsl(var(--muted))] text-[10px] font-medium">
                      {account.name
                        ? account.name.charAt(0).toUpperCase()
                        : "U"}
                    </div>
                  )}
                  <span className="text-xs font-medium truncate max-w-[100px]">
                    {account.name}
                  </span>
                </div>
                {onLogout && (
                  <button
                    onClick={onLogout}
                    className="flex items-center justify-center text-[hsl(var(--destructive))] hover:opacity-80 transition-opacity"
                    title={logoutLabel}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                      <polyline points="16 17 21 12 16 7" />
                      <line x1="21" y1="12" x2="9" y2="12" />
                    </svg>
                  </button>
                )}
              </div>
            </div>
          )}
          {footer}
        </div>
      )}
    </div>
  );
}

// ============================================================
// SidebarNavList (internal accordion-style nav)
// ============================================================
interface SidebarNavListProps {
  items: SidebarNavItem[];
  activeItemId?: string;
  onNavigate?: (itemId: string) => void;
  defaultExpandedGroups?: string[];
}

function SidebarNavList({
  items,
  activeItemId,
  onNavigate,
  defaultExpandedGroups,
}: SidebarNavListProps) {
  const [expanded, setExpanded] = useState<Record<string, boolean>>(() => {
    const map: Record<string, boolean> = {};
    (defaultExpandedGroups ?? items.map((i) => i.id)).forEach((id) => {
      map[id] = true;
    });
    return map;
  });
  const [localActiveItemId, setLocalActiveItemId] = useState<
    string | undefined
  >(activeItemId);

  useEffect(() => {
    if (activeItemId !== undefined) {
      setLocalActiveItemId(activeItemId);
    }
  }, [activeItemId]);

  const toggle = (id: string) => {
    setExpanded((prev: Record<string, boolean>) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };
  const resolvedActiveItemId = activeItemId ?? localActiveItemId;

  return (
    <div
      className="h-full flex-1 overflow-y-auto"
      style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
    >
      <style>{`.monkeys-sidebar-nav::-webkit-scrollbar { display: none; }`}</style>
      <div className="monkeys-sidebar-nav flex w-full flex-col gap-2 text-xs text-[hsl(var(--foreground))]">
        {items.map((item) => {
          if (item.children && item.children.length > 0) {
            const isExpanded = expanded[item.id] ?? true;
            return (
              <div key={item.id}>
                <NavButton
                  active={item.id === resolvedActiveItemId}
                  disabled={item.disabled}
                  icon={item.icon}
                  onClick={() => toggle(item.id)}
                  postfix={
                    <div className="flex flex-1 justify-end">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="shrink-0 text-[hsl(var(--foreground))] transition-transform duration-200"
                        style={{
                          transform: isExpanded
                            ? "rotate(90deg)"
                            : "rotate(0deg)",
                        }}
                      >
                        <path d="m9 18 6-6-6-6" />
                      </svg>
                    </div>
                  }
                >
                  {item.label}
                </NavButton>
                {isExpanded && !item.disabled && (
                  <div className="flex flex-col gap-1 mt-1">
                    {item.children.map((child) => (
                      <NavButton
                        key={child.id}
                        active={child.id === resolvedActiveItemId}
                        disabled={child.disabled}
                        onClick={() => {
                          setLocalActiveItemId(child.id);
                          onNavigate?.(child.id);
                        }}
                      >
                        {child.label}
                      </NavButton>
                    ))}
                  </div>
                )}
              </div>
            );
          }
          return (
            <NavButton
              key={item.id}
              icon={item.icon}
              active={item.id === resolvedActiveItemId}
              disabled={item.disabled}
              onClick={() => {
                setLocalActiveItemId(item.id);
                onNavigate?.(item.id);
              }}
            >
              {item.label}
            </NavButton>
          );
        })}
      </div>
    </div>
  );
}

// ============================================================
// AppLayout
// ============================================================
export interface AppLayoutProps {
  /** header 组件 */
  header?: ReactNode;
  /** sidebar 组件 */
  sidebar?: ReactNode;
  /** 主内容 */
  children: ReactNode;
  /** 是否 flush 布局（无内边距） */
  flush?: boolean;
  className?: string;
}

export function AppLayout({
  header,
  sidebar,
  children,
  flush = false,
  className,
}: AppLayoutProps) {
  return (
    <div
      className={cn(
        "flex h-screen flex-col gap-[var(--global-spacing,0.75rem)] overflow-hidden p-[var(--global-spacing,0.75rem)]",
        className,
      )}
      style={{ backgroundColor: "hsl(var(--muted))" }}
    >
      {header ? header : null}
      <div className="flex flex-1 gap-[var(--global-spacing,0.75rem)] overflow-hidden min-h-0">
        {sidebar ? sidebar : null}
        <main
          className={cn(
            "flex-1 min-h-0 overflow-hidden rounded-xl border border-[hsl(var(--input))] bg-[hsl(var(--background))]",
            !flush && "overflow-y-auto",
          )}
        >
          {flush ? (
            children
          ) : (
            <div className="h-full overflow-y-auto">{children}</div>
          )}
        </main>
      </div>
    </div>
  );
}

// ============================================================
// NavigationLayout
// ============================================================
export type NavigationMode = "sidebar" | "headbar";
export type HeadbarLayoutMode = "boxed" | "full-bleed";
export type HeadbarMenuRadius =
  "default" | "none" | "sm" | "md" | "lg" | "xl" | "full";
export type HeadbarNavPosition = "left" | "center" | "right";
export type HeadbarSurface = "card" | "glassy" | "ghost";

export interface LayoutNavItem {
  id: string;
  label: ReactNode;
  headbarLabel?: ReactNode;
  icon?: ReactNode;
  children?: LayoutNavItem[];
  disabled?: boolean;
  trailing?: ReactNode;
  headbarSecondaryAction?: ReactNode;
}

interface NavigationLayoutAction {
  id: string;
  label: ReactNode;
  title?: string;
  icon?: ReactNode;
  onClick?: () => void;
}

export interface HeadbarLayout {
  brandTitle?: ReactNode;
  heightPx?: number;
  layoutMode?: HeadbarLayoutMode;
  menuRadius?: HeadbarMenuRadius;
  navPosition?: HeadbarNavPosition;
  surface?: HeadbarSurface;
}

export interface NavigationLayoutProps {
  activeItemId?: string;
  activeRootId?: string;
  allowModeSwitch?: boolean;
  accountMenu?: ReactNode;
  children: ReactNode;
  className?: string;
  collapsedSidebarAccountMenu?: ReactNode;
  collapsedSidebarWidth?: string;
  contextualSidebar?: boolean;
  contextualSidebarHeader?: ReactNode;
  contextualSidebarWidth?: string;
  defaultMode?: NavigationMode;
  defaultSidebarCollapsed?: boolean;
  expandedSidebarWidth?: string;
  expandSidebarLabel?: string;
  flush?: boolean;
  headbarActions?: ReactNode;
  headbarLayout?: HeadbarLayout;
  logo?: AppHeaderLogo;
  mode?: NavigationMode;
  navItems: LayoutNavItem[];
  onModeChange?: (mode: NavigationMode) => void;
  onNavigate?: (itemId: string) => void;
  onSidebarCollapsedChange?: (collapsed: boolean) => void;
  renderHeadbarExtra?: ReactNode;
  collapseSidebarLabel?: string;
  sidebarCollapsed?: boolean;
  sidebarAccountMenu?: ReactNode;
  sidebarFooter?: ReactNode;
  sidebarHeader?: ReactNode;
  switchToHeadbarLabel?: string;
  switchToSidebarLabel?: string;
}

const NAVIGATION_LAYOUT_SIDEBAR_EXPANDED_WIDTH = "13.7rem";
const NAVIGATION_LAYOUT_SIDEBAR_COLLAPSED_WIDTH = "4.25rem";
const NAVIGATION_LAYOUT_CONTEXTUAL_SIDEBAR_WIDTH = "15rem";
const NAVIGATION_LAYOUT_TRANSITION_TIMING = "cubic-bezier(0.22, 1, 0.36, 1)";
const NAVIGATION_LAYOUT_HEADBAR_DEFAULT_HEIGHT_PX = 72;
const NAVIGATION_LAYOUT_HEADBAR_MIN_HEIGHT_PX = 56;
const NAVIGATION_LAYOUT_HEADBAR_MAX_HEIGHT_PX = 120;
const NAVIGATION_LAYOUT_HEADBAR_COMPACT_HEIGHT_THRESHOLD_PX = 64;
const NAVIGATION_LAYOUT_HEADBAR_BOXED_VERTICAL_PADDING_PX = 12;
const NAVIGATION_LAYOUT_HEADBAR_COMPACT_VERTICAL_PADDING_PX = 8;
const NAVIGATION_LAYOUT_HEADBAR_LOGO_DEFAULT_SIZE_PX = 44;
const NAVIGATION_LAYOUT_HEADBAR_LOGO_COMPACT_SIZE_PX = 36;
const NAVIGATION_LAYOUT_DEFAULT_APPEARANCE: BaseAppearance = {
  preset: "soft",
  density: "compact",
  radius: "lg",
  border: "subtle",
  background: "soft",
};

const NAVIGATION_LAYOUT_MENU_RADIUS_CLASS_MAP: Record<
  Exclude<HeadbarMenuRadius, "default">,
  { all: string; left: string; right: string }
> = {
  none: { all: "rounded-none", left: "!rounded-none", right: "!rounded-none" },
  sm: {
    all: "rounded-sm",
    left: "!rounded-l-sm !rounded-r-none",
    right: "!rounded-l-none !rounded-r-sm",
  },
  md: {
    all: "rounded-md",
    left: "!rounded-l-md !rounded-r-none",
    right: "!rounded-l-none !rounded-r-md",
  },
  lg: {
    all: "rounded-lg",
    left: "!rounded-l-lg !rounded-r-none",
    right: "!rounded-l-none !rounded-r-lg",
  },
  xl: {
    all: "rounded-xl",
    left: "!rounded-l-xl !rounded-r-none",
    right: "!rounded-l-none !rounded-r-xl",
  },
  full: {
    all: "rounded-full",
    left: "!rounded-l-full !rounded-r-none",
    right: "!rounded-l-none !rounded-r-full",
  },
};

function clampHeadbarHeight(value?: number) {
  if (typeof value !== "number" || !Number.isFinite(value))
    return NAVIGATION_LAYOUT_HEADBAR_DEFAULT_HEIGHT_PX;
  return Math.min(
    NAVIGATION_LAYOUT_HEADBAR_MAX_HEIGHT_PX,
    Math.max(NAVIGATION_LAYOUT_HEADBAR_MIN_HEIGHT_PX, Math.round(value)),
  );
}

function getHeadbarVerticalPaddingPx(heightPx: number) {
  return heightPx <= NAVIGATION_LAYOUT_HEADBAR_COMPACT_HEIGHT_THRESHOLD_PX
    ? NAVIGATION_LAYOUT_HEADBAR_COMPACT_VERTICAL_PADDING_PX
    : NAVIGATION_LAYOUT_HEADBAR_BOXED_VERTICAL_PADDING_PX;
}

function getHeadbarLogoSizePx(heightPx: number) {
  return heightPx <= NAVIGATION_LAYOUT_HEADBAR_COMPACT_HEIGHT_THRESHOLD_PX
    ? NAVIGATION_LAYOUT_HEADBAR_LOGO_COMPACT_SIZE_PX
    : NAVIGATION_LAYOUT_HEADBAR_LOGO_DEFAULT_SIZE_PX;
}

function getHeadbarMenuRadiusClass(
  menuRadius: HeadbarMenuRadius = "default",
  part: "all" | "left" | "right" = "all",
) {
  if (menuRadius === "default") {
    if (part === "left") return "rounded-l-lg rounded-r-none";
    if (part === "right") return "rounded-l-none rounded-r-lg";
    return "rounded-lg";
  }
  return NAVIGATION_LAYOUT_MENU_RADIUS_CLASS_MAP[menuRadius][part];
}

function getHeadbarFrameClass(
  layoutMode: HeadbarLayoutMode = "boxed",
  navPosition: HeadbarNavPosition = "center",
) {
  const columns =
    navPosition === "center"
      ? "grid-cols-[minmax(max-content,1fr)_minmax(0,max-content)_minmax(max-content,1fr)]"
      : "grid-cols-[max-content_minmax(0,1fr)_max-content]";
  return layoutMode === "full-bleed"
    ? cn(
        "sticky top-0 z-40 grid w-full shrink-0 items-center gap-4 rounded-none border-b border-x-0 border-t-0 border-[hsl(var(--input))] bg-[hsl(var(--background))] p-[var(--global-spacing,0.75rem)]",
        columns,
      )
    : cn(
        "sticky top-0 z-40 grid w-full shrink-0 items-center gap-4 rounded-xl border border-[hsl(var(--input))] bg-[hsl(var(--background))] p-[var(--global-spacing,0.75rem)]",
        columns,
      );
}

function getHeadbarFrameStyle(
  layoutMode: HeadbarLayoutMode = "boxed",
  heightPx: number,
  surface: HeadbarSurface = "card",
): CSSProperties {
  const verticalPaddingPx = getHeadbarVerticalPaddingPx(heightPx);
  const fallbackBackground = "hsl(var(--background))";
  const style: CSSProperties = {
    height: `var(--monkeys-component-navigation-headbar-height, ${heightPx}px)`,
    minHeight: `var(--monkeys-component-navigation-headbar-height, ${heightPx}px)`,
    paddingTop: `${verticalPaddingPx}px`,
    paddingBottom: `${verticalPaddingPx}px`,
    background: `var(--monkeys-component-navigation-headbar-frame-background, ${fallbackBackground})`,
    color:
      "var(--monkeys-component-navigation-headbar-frame-foreground, hsl(var(--foreground)))",
    borderColor:
      surface === "glassy"
        ? "var(--monkeys-component-navigation-headbar-frame-border-color, rgb(255 255 255 / 0.5))"
        : "var(--monkeys-component-navigation-headbar-frame-border-color, hsl(var(--input)))",
    borderRadius:
      layoutMode === "full-bleed"
        ? "0"
        : "var(--monkeys-component-navigation-headbar-frame-radius, var(--radius, 0.75rem))",
    boxShadow:
      surface === "glassy"
        ? "var(--monkeys-component-navigation-headbar-frame-shadow, inset 0 0 0 0 rgb(0 0 0 / 0.1))"
        : "var(--monkeys-component-navigation-headbar-frame-shadow, none)",
    backdropFilter: `blur(var(--monkeys-component-navigation-headbar-frame-backdrop-blur, ${surface === "glassy" ? "45px" : "0px"}))`,
  };

  if (layoutMode === "full-bleed") {
    style.marginLeft = "calc(var(--global-spacing,0.75rem) * -1)";
    style.marginRight = "calc(var(--global-spacing,0.75rem) * -1)";
    style.marginTop = "calc(var(--global-spacing,0.75rem) * -1)";
    style.width = "calc(100% + (var(--global-spacing,0.75rem) * 2))";
  }

  return style;
}

function getHeadbarNavClass(navPosition: HeadbarNavPosition = "center") {
  if (navPosition === "left") return "justify-start";
  if (navPosition === "right") return "justify-end";
  return "justify-center";
}

function getHeadbarItemStyle(
  active: boolean,
  useTokenRadius = true,
): CSSProperties {
  return {
    height: "var(--monkeys-component-navigation-headbar-item-height, 2.25rem)",
    borderRadius: useTokenRadius
      ? "var(--monkeys-component-navigation-headbar-item-radius, var(--radius, 0.5rem))"
      : undefined,
    paddingInline:
      "var(--monkeys-component-navigation-headbar-item-padding-inline, 0.75rem)",
    columnGap: "var(--monkeys-component-navigation-headbar-item-gap, 0.375rem)",
    color: active
      ? "var(--monkeys-component-navigation-headbar-item-active-foreground, hsl(var(--foreground)))"
      : "var(--monkeys-component-navigation-headbar-item-foreground, hsl(var(--muted-foreground)))",
    background: active
      ? "var(--monkeys-component-navigation-headbar-item-active-background, hsl(var(--muted)))"
      : "transparent",
    borderColor: active
      ? "var(--monkeys-component-navigation-headbar-item-active-border-color, transparent)"
      : "transparent",
    fontSize:
      "var(--monkeys-component-navigation-headbar-item-font-size, 0.875rem)",
    fontWeight:
      "var(--monkeys-component-navigation-headbar-item-font-weight, 500)",
  };
}

function getHeadbarCompoundStyle(
  active: boolean,
  useTokenRadius = true,
): CSSProperties {
  return {
    ...getHeadbarItemStyle(active, useTokenRadius),
    paddingInline: undefined,
    columnGap: undefined,
  };
}

function findNavigationRootId(
  items: LayoutNavItem[],
  activeItemId?: string,
): string | undefined {
  for (const item of items) {
    if (item.id === activeItemId) return item.id;
    if (containsNavigationItem(item, activeItemId)) return item.id;
  }
  return activeItemId;
}

function containsNavigationItem(
  item: LayoutNavItem,
  itemId?: string,
): boolean {
  if (!itemId) return false;
  if (item.id === itemId) return true;
  return item.children?.some((child) => containsNavigationItem(child, itemId)) ?? false;
}

function findActiveNavigationBranch(
  item: LayoutNavItem,
  activeItemId?: string,
): LayoutNavItem | undefined {
  return item.children?.find((child) => containsNavigationItem(child, activeItemId));
}

function getNavigationActionTitle(action: NavigationLayoutAction) {
  return (
    action.title ||
    (typeof action.label === "string" ? action.label : undefined)
  );
}

function NavigationLayoutActionButtons({
  actions,
  compact,
}: {
  actions: NavigationLayoutAction[];
  compact: boolean;
}) {
  if (!actions.length) return null;

  if (compact) {
    return (
      <div className="flex flex-col items-center gap-2">
        {actions.map((action) => {
          const title = getNavigationActionTitle(action);
          return (
            <BaseTooltip
              key={action.id}
              appearance={NAVIGATION_LAYOUT_DEFAULT_APPEARANCE}
              content={action.label}
              placement="right"
              portal
            >
              <BaseButton
                appearance={NAVIGATION_LAYOUT_DEFAULT_APPEARANCE}
                tone="muted"
                iconOnly
                icon={action.icon}
                onClick={action.onClick}
                title={title}
                ariaLabel={title}
                className={cn(
                  "h-10 w-10 rounded-lg border-transparent text-[hsl(var(--muted-foreground))] shadow-none",
                  "hover:-translate-y-0.5 hover:border-[hsl(var(--input))]/80 hover:bg-[hsl(var(--muted))] hover:text-[hsl(var(--foreground))]",
                  "active:translate-y-0 active:scale-[0.96]",
                )}
              />
            </BaseTooltip>
          );
        })}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2">
      {actions.map((action) => (
        <BaseButton
          key={action.id}
          appearance={NAVIGATION_LAYOUT_DEFAULT_APPEARANCE}
          tone="muted"
          icon={action.icon}
          label={<span className="truncate">{action.label}</span>}
          onClick={action.onClick}
          title={getNavigationActionTitle(action)}
          className={cn(
            "h-9 w-full justify-start gap-2.5 rounded-lg border-transparent px-3 text-left text-[13px] text-[hsl(var(--muted-foreground))] shadow-none",
            "hover:-translate-y-0.5 hover:border-[hsl(var(--input))]/80 hover:bg-[hsl(var(--muted))]/70 hover:text-[hsl(var(--foreground))]",
            "active:translate-y-0 active:scale-[0.985]",
          )}
        />
      ))}
    </div>
  );
}

function SidebarNavigationList({
  activeItemId,
  items,
  onNavigate,
  revealRequest,
}: {
  activeItemId?: string;
  items: LayoutNavItem[];
  onNavigate?: (itemId: string) => void;
  revealRequest?: { id: string; nonce: number } | null;
}) {
  const navRef = useRef<HTMLDivElement | null>(null);
  const [expandedByRoot, setExpandedByRoot] = useState<Record<string, boolean>>(
    {},
  );
  const canReveal = revealRequest
    ? items.some((item) => containsNavigationItem(item, revealRequest.id) && !item.disabled)
    : false;

  const toggleRoot = (id: string) => {
    setExpandedByRoot((current) => ({
      ...current,
      [id]: !current[id],
    }));
  };

  useEffect(() => {
    if (!revealRequest || !canReveal) return;
    setExpandedByRoot((current) => ({ ...current, [revealRequest.id]: true }));

    const frameId = window.requestAnimationFrame(() => {
      const targetElement = navRef.current?.querySelector(
        `[data-navigation-sidebar-item-id="${CSS.escape(revealRequest.id)}"]`,
      );
      if (targetElement instanceof HTMLElement) {
        targetElement.scrollIntoView({
          behavior: "auto",
          block: "nearest",
          inline: "nearest",
        });
      }
    });

    return () => window.cancelAnimationFrame(frameId);
  }, [canReveal, revealRequest]);

  const renderItems = (navigationItems: LayoutNavItem[], depth = 0): ReactNode =>
    navigationItems.map((item) => {
      const isGroup = Boolean(item.children?.length);
      const containsActiveItem = containsNavigationItem(item, activeItemId);
      const isExpanded =
        !item.disabled &&
        (expandedByRoot[item.id] ?? containsActiveItem);

      return (
        <div key={item.id} data-navigation-sidebar-item-id={item.id}>
          <NavButton
            active={containsActiveItem}
            className={depth > 0 ? "pl-3" : undefined}
            disabled={item.disabled}
            icon={item.icon}
            onClick={() =>
              isGroup ? toggleRoot(item.id) : onNavigate?.(item.id)
            }
            postfix={isGroup ? (
              <div className="flex flex-1 justify-end">
                <HeadbarChevronIcon
                  open={false}
                  sideways
                  className="text-[hsl(var(--foreground))] transition-transform duration-200"
                  style={{ transform: isExpanded ? "rotate(90deg)" : "rotate(0deg)" }}
                />
              </div>
            ) : undefined}
          >
            {item.label}
          </NavButton>
          {isGroup && isExpanded ? (
            <div className="mt-1 ml-3 flex flex-col gap-1 border-l border-[hsl(var(--input))] pl-2">
              {renderItems(item.children ?? [], depth + 1)}
            </div>
          ) : null}
        </div>
      );
    });

  return (
    <div
      className="h-full flex-1 overflow-y-auto"
      style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
    >
      <style>{`.monkeys-navigation-sidebar-nav::-webkit-scrollbar { display: none; }`}</style>
      <div
        ref={navRef}
        className="monkeys-navigation-sidebar-nav flex w-full flex-col gap-2 text-xs text-[hsl(var(--foreground))]"
      >
        {renderItems(items)}
      </div>
    </div>
  );
}

function SidebarBrand({
  collapsed,
  logo,
  title,
}: {
  collapsed?: boolean;
  logo?: AppHeaderLogo;
  title?: ReactNode;
}) {
  if (!logo) return null;

  const brandTitle = title ?? logo.title;
  const logoMark = (
    <div
      className={cn(
        "flex h-9 w-9 shrink-0 items-center justify-center overflow-hidden rounded-lg",
        logo.url
          ? "bg-transparent"
          : "bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))]",
      )}
    >
      {logo.url ? (
        <img
          src={logo.url}
          alt={logo.title}
          className="h-full w-full object-contain"
        />
      ) : (
        <span className="text-sm font-bold">{logo.title.charAt(0)}</span>
      )}
    </div>
  );

  if (collapsed) {
    return (
      <BaseTooltip
        appearance={NAVIGATION_LAYOUT_DEFAULT_APPEARANCE}
        content={brandTitle}
        placement="right"
        portal
      >
        <BaseButton
          appearance={NAVIGATION_LAYOUT_DEFAULT_APPEARANCE}
          ariaLabel={typeof brandTitle === "string" ? brandTitle : logo.title}
          className="h-10 w-10 rounded-lg border-transparent bg-transparent p-0 shadow-none hover:bg-[hsl(var(--muted))]/60"
          icon={logoMark}
          iconOnly
          onClick={logo.onClick}
          title={typeof brandTitle === "string" ? brandTitle : logo.title}
          tone="muted"
        />
      </BaseTooltip>
    );
  }

  return (
    <BaseButton
      appearance={NAVIGATION_LAYOUT_DEFAULT_APPEARANCE}
      ariaLabel={logo.title}
      className="h-auto w-full justify-start gap-3 border-transparent bg-transparent p-0 text-left shadow-none hover:bg-transparent"
      classNames={{ icon: "h-9 w-9" }}
      icon={logoMark}
      label={
        <span className="min-w-0 truncate text-sm font-semibold text-[hsl(var(--foreground))]">
          {brandTitle}
        </span>
      }
      onClick={logo.onClick}
      title={typeof brandTitle === "string" ? brandTitle : logo.title}
      tone="muted"
    />
  );
}

function Sidebar({
  accountMenu,
  activeItemId,
  actions = [],
  collapsed,
  collapsedAccountMenu,
  footer,
  header,
  items,
  logo,
  onCollapsedChange,
  onNavigate,
  onRevealRoot,
  revealRequest,
  title,
}: {
  accountMenu?: ReactNode;
  activeItemId?: string;
  actions?: NavigationLayoutAction[];
  collapsed?: boolean;
  collapsedAccountMenu?: ReactNode;
  footer?: ReactNode;
  header?: ReactNode;
  items: LayoutNavItem[];
  logo?: AppHeaderLogo;
  onCollapsedChange?: (collapsed: boolean) => void;
  onNavigate?: (itemId: string) => void;
  onRevealRoot?: (rootId: string) => void;
  revealRequest?: { id: string; nonce: number } | null;
  title?: ReactNode;
}) {
  if (collapsed) {
    const activeRootId = findNavigationRootId(items, activeItemId);

    return (
      <div className="flex h-full min-h-0 w-full flex-col rounded-xl border border-[hsl(var(--input))] bg-[hsl(var(--background))] p-2 shadow-sm">
        <div className="mb-3 flex shrink-0 justify-center">
          {header ?? <SidebarBrand collapsed logo={logo} title={title} />}
        </div>
        <div
          className="flex min-h-0 flex-1 flex-col items-center gap-2 overflow-y-auto pb-3"
          style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
        >
          {items.map((item) => {
            const isActive = activeRootId === item.id;
            return (
              <BaseTooltip
                key={item.id}
                appearance={NAVIGATION_LAYOUT_DEFAULT_APPEARANCE}
                content={item.label}
                placement="right"
                portal
              >
                <BaseButton
                  appearance={NAVIGATION_LAYOUT_DEFAULT_APPEARANCE}
                  tone="muted"
                  active={isActive}
                  disabled={item.disabled}
                  iconOnly
                  icon={item.icon}
                  onClick={() => {
                    if (item.disabled) return;
                    onRevealRoot?.(item.id);
                    onCollapsedChange?.(false);
                    if (item.children?.length) return;
                    onNavigate?.(item.id);
                  }}
                  title={
                    typeof item.label === "string" ? item.label : undefined
                  }
                  ariaLabel={
                    typeof item.label === "string" ? item.label : item.id
                  }
                  className={cn(
                    "h-10 w-10 rounded-lg border text-[hsl(var(--muted-foreground))] shadow-none",
                    "hover:-translate-y-0.5 active:translate-y-0 active:scale-[0.96]",
                    isActive
                      ? "border-[hsl(var(--input))] bg-[hsl(var(--muted))] text-[hsl(var(--foreground))] shadow-sm"
                      : "border-transparent hover:border-[hsl(var(--input))]/80 hover:bg-[hsl(var(--muted))]/70 hover:text-[hsl(var(--foreground))]",
                  )}
                />
              </BaseTooltip>
            );
          })}
        </div>
        <div className="-mx-2 mt-2 border-t border-[hsl(var(--input))] bg-[hsl(var(--background))] px-2 pt-2.5 pb-0.5">
          <NavigationLayoutActionButtons actions={actions} compact />
          {collapsedAccountMenu ? (
            <div className="mt-2 flex justify-center border-t border-[hsl(var(--input))] pt-2">
              {collapsedAccountMenu}
            </div>
          ) : null}
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-0 w-full flex-col justify-between gap-0 overflow-y-hidden rounded-xl border border-[hsl(var(--input))] bg-[hsl(var(--background))] px-5 pt-5 pb-1.5">
      <div className="mb-4 shrink-0">
        {header ?? <SidebarBrand logo={logo} title={title} />}
      </div>
      <SidebarNavigationList
        activeItemId={activeItemId}
        items={items}
        onNavigate={onNavigate}
        revealRequest={revealRequest}
      />
      <div className="-mx-5 mt-2 border-t border-[hsl(var(--input))] bg-[hsl(var(--background))] px-4 pt-2.5 pb-1">
        <NavigationLayoutActionButtons actions={actions} compact={false} />
        {footer}
        {accountMenu ? (
          <div className="mt-2 border-t border-[hsl(var(--input))] pt-2">
            {accountMenu}
          </div>
        ) : null}
      </div>
    </div>
  );
}

function HeadbarChevronIcon({
  className,
  open,
  sideways = false,
  style,
}: {
  className?: string;
  open: boolean;
  sideways?: boolean;
  style?: CSSProperties;
}) {
  return (
    <svg
      aria-hidden="true"
      className={cn(
        "h-4 w-4 shrink-0",
        !sideways && "transition-transform duration-200",
        !sideways && open && "rotate-180",
        className,
      )}
      style={style}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d={sideways ? "m9 18 6-6-6-6" : "m6 9 6 6 6-6"} />
    </svg>
  );
}

function getHeadbarSummaryTitle(
  rootLabel: ReactNode,
  childLabel: ReactNode,
) {
  if (typeof rootLabel !== "string" || typeof childLabel !== "string") {
    return undefined;
  }

  const rootText = rootLabel.trim();
  const childText = childLabel.trim();
  return rootText && childText ? `${rootText} ${childText}` : undefined;
}

const HEADBAR_DROPDOWN_ITEM_CLASS =
  "relative flex w-full min-w-0 cursor-pointer select-none items-center gap-2.5 px-3 py-2 text-sm outline-none transition-colors hover:!bg-[var(--monkeys-component-navigation-headbar-item-hover-background,hsl(var(--accent)))] hover:!text-[var(--monkeys-component-navigation-headbar-item-hover-foreground,hsl(var(--accent-foreground)))] focus:!bg-[var(--monkeys-component-navigation-headbar-item-hover-background,hsl(var(--accent)))] focus:!text-[var(--monkeys-component-navigation-headbar-item-hover-foreground,hsl(var(--accent-foreground)))] data-[highlighted]:!bg-[var(--monkeys-component-navigation-headbar-item-hover-background,hsl(var(--accent)))] data-[highlighted]:!text-[var(--monkeys-component-navigation-headbar-item-hover-foreground,hsl(var(--accent-foreground)))]";

const HEADBAR_DROPDOWN_CONTENT_STYLE: CSSProperties = {
  width: "max-content",
  maxWidth: "min(20rem, var(--radix-dropdown-menu-content-available-width))",
  background:
    "var(--monkeys-component-navigation-headbar-menu-background, hsl(var(--monkeys-color-menu-surface)))",
  borderColor:
    "var(--monkeys-component-navigation-headbar-menu-border-color, var(--monkeys-component-overlay-border-color, hsl(var(--monkeys-color-border))))",
  color:
    "var(--monkeys-component-navigation-headbar-menu-foreground, hsl(var(--monkeys-color-popover-foreground)))",
  boxShadow:
    "var(--monkeys-component-navigation-headbar-menu-shadow, var(--monkeys-component-overlay-menu-shadow, 0 10px 15px -3px rgb(0 0 0 / 0.1)))",
  backdropFilter:
    "blur(var(--monkeys-component-navigation-headbar-menu-backdrop-blur, var(--monkeys-component-overlay-backdrop-blur, 0px)))",
  WebkitBackdropFilter:
    "blur(var(--monkeys-component-navigation-headbar-menu-backdrop-blur, var(--monkeys-component-overlay-backdrop-blur, 0px)))",
};

function HeadbarDropdownItems({
  activeItemId,
  items,
  menuContentRadiusClass,
  menuItemRadiusClass,
  onNavigate,
  onNavigateComplete,
  useTokenRadius,
}: {
  activeItemId?: string;
  items: LayoutNavItem[];
  menuContentRadiusClass: string;
  menuItemRadiusClass: string;
  onNavigate?: (itemId: string) => void;
  onNavigateComplete: () => void;
  useTokenRadius: boolean;
}) {
  const componentAttributes = useMonkeysComponentAttributes();

  return items.map((item) => {
    const label = (
      <>
        {item.icon ? (
          <span className="inline-flex h-4 w-4 shrink-0 items-center justify-center text-[hsl(var(--muted-foreground))]">
            {item.icon}
          </span>
        ) : null}
        <span
          className={cn(
            "min-w-0 truncate",
            Boolean(item.children?.length) && "flex-1",
          )}
        >
          {item.headbarLabel ?? item.label}
        </span>
      </>
    );

    if (item.children?.length) {
      return (
        <DropdownMenu.Sub key={item.id}>
          <DropdownMenu.SubTrigger
            disabled={item.disabled}
            className={cn(HEADBAR_DROPDOWN_ITEM_CLASS, menuItemRadiusClass)}
          >
            {label}
            <HeadbarChevronIcon open={false} sideways />
          </DropdownMenu.SubTrigger>
          <DropdownMenu.Portal>
            <DropdownMenu.SubContent
              {...componentAttributes}
              sideOffset={4}
              alignOffset={-6}
              data-monkeys-component="navigation-headbar-menu"
              data-slot="content"
              data-surface="menu"
              className={cn(
                "z-50 border p-1.5 animate-in fade-in-0 zoom-in-95",
                getOverlaySurfaceClassName("menu"),
                menuContentRadiusClass,
              )}
              style={{
                ...HEADBAR_DROPDOWN_CONTENT_STYLE,
                minWidth: "12rem",
              }}
            >
              <HeadbarDropdownItems
                activeItemId={activeItemId}
                items={item.children}
                menuContentRadiusClass={menuContentRadiusClass}
                menuItemRadiusClass={menuItemRadiusClass}
                onNavigate={onNavigate}
                onNavigateComplete={onNavigateComplete}
                useTokenRadius={useTokenRadius}
              />
            </DropdownMenu.SubContent>
          </DropdownMenu.Portal>
        </DropdownMenu.Sub>
      );
    }

    return (
      <DropdownMenu.Item
        key={item.id}
        disabled={item.disabled}
        className={cn(HEADBAR_DROPDOWN_ITEM_CLASS, menuItemRadiusClass)}
        style={getHeadbarItemStyle(item.id === activeItemId, useTokenRadius)}
        onSelect={() => {
          if (item.disabled) return;
          onNavigateComplete();
          onNavigate?.(item.id);
        }}
      >
        {label}
        {item.trailing}
      </DropdownMenu.Item>
    );
  });
}

function HeadbarNavMenuItem({
  activeItemId,
  compound,
  isActive,
  item,
  label,
  menuButtonRadiusClass,
  menuContentRadiusClass,
  menuItemRadiusClass,
  onNavigate,
  title,
  useTokenRadius,
}: {
  activeItemId?: string;
  compound?: boolean;
  isActive: boolean;
  item: LayoutNavItem;
  label: ReactNode;
  menuButtonRadiusClass: string;
  menuContentRadiusClass: string;
  menuItemRadiusClass: string;
  onNavigate?: (itemId: string) => void;
  title?: string;
  useTokenRadius: boolean;
}) {
  const [open, setOpen] = useState(false);
  const componentAttributes = useMonkeysComponentAttributes();
  const isOpen = !item.disabled && open;
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const wasOpenOnPointerDownRef = useRef(false);

  const clearCloseTimer = () => {
    if (!closeTimerRef.current) return;
    clearTimeout(closeTimerRef.current);
    closeTimerRef.current = null;
  };

  const openMenu = () => {
    if (item.disabled) return;
    clearCloseTimer();
    setOpen(true);
  };

  const scheduleCloseMenu = () => {
    clearCloseTimer();
    closeTimerRef.current = setTimeout(() => {
      setOpen(false);
      closeTimerRef.current = null;
    }, 120);
  };

  const navigateToFirstEnabledChild = () => {
    const firstEnabledChild = item.children?.find((child) => !child.disabled);
    if (!firstEnabledChild) return false;

    setOpen(false);
    onNavigate?.(firstEnabledChild.id);
    return true;
  };

  useEffect(() => clearCloseTimer, []);

  return (
    <DropdownMenu.Root
      modal={false}
      open={isOpen}
      onOpenChange={(nextOpen) => setOpen(!item.disabled && nextOpen)}
    >
      <DropdownMenu.Trigger asChild>
        <BaseButton
          appearance={NAVIGATION_LAYOUT_DEFAULT_APPEARANCE}
          tone="muted"
          active={isActive || isOpen}
          icon={item.icon}
          endIcon={<HeadbarChevronIcon open={isOpen} />}
          label={label}
          title={title}
          disabled={item.disabled}
          onClick={() => {
            const wasOpenOnPointerDown = wasOpenOnPointerDownRef.current;
            wasOpenOnPointerDownRef.current = false;
            if (wasOpenOnPointerDown && navigateToFirstEnabledChild()) return;
            setOpen(true);
          }}
          onPointerDown={() => {
            wasOpenOnPointerDownRef.current = isOpen;
          }}
          onPointerCancel={() => {
            wasOpenOnPointerDownRef.current = false;
          }}
          onKeyDown={(event) => {
            if (
              !isOpen ||
              (event.key !== "Enter" && event.key !== " ")
            )
              return;
            if (navigateToFirstEnabledChild()) event.preventDefault();
          }}
          onMouseEnter={openMenu}
          onMouseLeave={scheduleCloseMenu}
          style={getHeadbarItemStyle(
            isActive || isOpen,
            compound ? false : useTokenRadius,
          )}
          classNames={{
            icon: "text-[inherit] [&_*]:!text-[inherit] [&_svg]:!stroke-current",
            label:
              "flex min-w-0 items-center text-[inherit] [&_*]:!text-[inherit]",
          }}
          className={cn(
            "max-w-[12rem] shrink-0 border shadow-none",
            compound && "!rounded-none !border-0 !bg-transparent",
            menuButtonRadiusClass,
            isActive || isOpen
              ? ""
              : "hover:!bg-[var(--monkeys-component-navigation-headbar-item-hover-background,hsl(var(--muted)))] hover:!text-[var(--monkeys-component-navigation-headbar-item-hover-foreground,hsl(var(--foreground)))]",
          )}
        />
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content
          {...componentAttributes}
          align="start"
          sideOffset={5}
          data-monkeys-component="navigation-headbar-menu"
          data-slot="content"
          data-surface="menu"
          onCloseAutoFocus={(event) => event.preventDefault()}
          onMouseEnter={openMenu}
          onMouseLeave={scheduleCloseMenu}
          className={cn(
            "z-50 border p-1.5 animate-in fade-in-0 zoom-in-95",
            getOverlaySurfaceClassName("menu"),
            menuContentRadiusClass,
          )}
          style={{
            ...HEADBAR_DROPDOWN_CONTENT_STYLE,
            minWidth: "var(--radix-dropdown-menu-trigger-width)",
          }}
        >
          <HeadbarDropdownItems
            activeItemId={activeItemId}
            items={item.children ?? []}
            menuContentRadiusClass={menuContentRadiusClass}
            menuItemRadiusClass={menuItemRadiusClass}
            onNavigate={onNavigate}
            onNavigateComplete={() => setOpen(false)}
            useTokenRadius={useTokenRadius}
          />
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}

function blockEvent(event: SyntheticEvent) {
  event.preventDefault();
  event.stopPropagation();
}

export interface NavigationHeadbarMenuProps {
  activeItemId?: string;
  activeRootId?: string;
  items: LayoutNavItem[];
  menuRadius?: HeadbarMenuRadius;
  onNavigate?: (itemId: string) => void;
}

export function NavigationHeadbarMenu({
  activeItemId,
  activeRootId,
  items,
  menuRadius = "default",
  onNavigate,
}: NavigationHeadbarMenuProps) {
  const menuButtonRadiusClass = getHeadbarMenuRadiusClass(menuRadius);
  const menuContentRadiusClass = getHeadbarMenuRadiusClass(menuRadius);
  const menuItemRadiusClass = getHeadbarMenuRadiusClass(menuRadius);
  const useTokenRadius = menuRadius === "default";

  return (
    <div className="flex w-max min-w-0 shrink-0 items-center gap-1">
      {items.map((item) => {
        const isActive = activeRootId === item.id || activeItemId === item.id;
        const hasSecondaryAction = Boolean(item.headbarSecondaryAction);
        const rootLabel = item.headbarLabel ?? item.label;
        const activeChild = findActiveNavigationBranch(item, activeItemId);
        const activeChildLabel = activeChild
          ? (activeChild.headbarLabel ?? activeChild.label)
          : undefined;
        const label = activeChild ? (
          <span
            data-navigation-headbar-selected-child={activeChild.id}
            className="flex min-w-0 items-center gap-1.5 whitespace-nowrap"
          >
            <span className="shrink-0">{rootLabel}</span>
            <span className="sr-only">: </span>
            <span className="min-w-0 truncate">{activeChildLabel}</span>
          </span>
        ) : (
          <span className="truncate whitespace-nowrap">{rootLabel}</span>
        );
        const title = activeChild
          ? getHeadbarSummaryTitle(rootLabel, activeChildLabel)
          : undefined;

        const primaryAction = item.children?.length ? (
          <HeadbarNavMenuItem
            activeItemId={activeItemId}
            compound={hasSecondaryAction}
            isActive={isActive}
            item={item}
            label={label}
            menuButtonRadiusClass={menuButtonRadiusClass}
            menuContentRadiusClass={menuContentRadiusClass}
            menuItemRadiusClass={menuItemRadiusClass}
            onNavigate={onNavigate}
            title={title}
            useTokenRadius={useTokenRadius}
          />
        ) : (
          <BaseButton
            appearance={NAVIGATION_LAYOUT_DEFAULT_APPEARANCE}
            tone="muted"
            active={isActive}
            disabled={item.disabled}
            icon={item.icon}
            label={
              <span className="flex min-w-0 items-center gap-1.5">
                <span className="truncate whitespace-nowrap">{label}</span>
                {item.trailing}
              </span>
            }
            onClick={() => onNavigate?.(item.id)}
            style={getHeadbarItemStyle(
              isActive,
              hasSecondaryAction ? false : useTokenRadius,
            )}
            classNames={{
              icon: "text-[inherit] [&_*]:!text-[inherit] [&_svg]:!stroke-current",
              label: "text-[inherit] [&_*]:!text-[inherit]",
            }}
            className={cn(
              "max-w-[12rem] shrink-0 border shadow-none",
              hasSecondaryAction &&
                "!rounded-none !border-0 !bg-transparent",
              menuButtonRadiusClass,
              isActive
                ? ""
                : "hover:!bg-[var(--monkeys-component-navigation-headbar-item-hover-background,hsl(var(--muted)))] hover:!text-[var(--monkeys-component-navigation-headbar-item-hover-foreground,hsl(var(--foreground)))]",
            )}
          />
        );

        if (!hasSecondaryAction) {
          return <Fragment key={item.id}>{primaryAction}</Fragment>;
        }

        return (
          <div
            key={item.id}
            data-navigation-headbar-compound-item={item.id}
            aria-disabled={item.disabled}
            className={cn(
              "inline-flex min-w-0 shrink-0 items-center gap-0 overflow-hidden border shadow-none",
              menuButtonRadiusClass,
              isActive
                ? ""
                : "hover:!bg-[var(--monkeys-component-navigation-headbar-item-hover-background,hsl(var(--muted)))] hover:!text-[var(--monkeys-component-navigation-headbar-item-hover-foreground,hsl(var(--foreground)))]",
            )}
            style={getHeadbarCompoundStyle(isActive, useTokenRadius)}
          >
            <span
              data-navigation-headbar-primary-action=""
              className="inline-flex min-w-0 shrink-0"
            >
              {primaryAction}
            </span>
            <span
              data-navigation-headbar-secondary-action={item.id}
              {...(item.disabled && {
                inert: "",
                onClickCapture: blockEvent,
                onKeyDownCapture: blockEvent,
                onKeyUpCapture: blockEvent,
                onPointerDownCapture: blockEvent,
              })}
              className={cn(
                "inline-flex min-w-0 shrink-0 items-center",
                item.disabled && "!cursor-not-allowed opacity-60",
              )}
            >
              {item.headbarSecondaryAction}
            </span>
          </div>
        );
      })}
    </div>
  );
}

export interface NavigationHeadbarProps {
  activeItemId?: string;
  activeRootId?: string;
  actions?: ReactNode;
  accountMenu?: ReactNode;
  className?: string;
  extra?: ReactNode;
  layout?: HeadbarLayout;
  brand?: ReactNode;
  logo?: AppHeaderLogo;
  navigation?: ReactNode;
  navItems?: LayoutNavItem[];
  onNavigate?: (itemId: string) => void;
}

export function NavigationHeadbar({
  activeItemId,
  activeRootId,
  actions,
  accountMenu,
  className,
  extra,
  layout,
  brand,
  logo,
  navigation,
  navItems = [],
  onNavigate,
}: NavigationHeadbarProps) {
  const heightPx = clampHeadbarHeight(layout?.heightPx);
  const layoutMode = layout?.layoutMode ?? "boxed";
  const menuRadius = layout?.menuRadius ?? "default";
  const surface = layout?.surface ?? "card";
  const logoHeightPx = getHeadbarLogoSizePx(heightPx);
  const frameStyle = getHeadbarFrameStyle(layoutMode, heightPx, surface);
  const showBrandTitle = Boolean(layout?.brandTitle);

  return (
    <header
      className={cn(
        getHeadbarFrameClass(layoutMode, layout?.navPosition),
        className,
      )}
      data-navigation-headbar=""
      data-navigation-headbar-surface={surface}
      style={frameStyle}
    >
      <div className="flex min-w-0 items-center justify-start">
        {brand ??
          (logo ? (
            <BaseButton
              appearance={NAVIGATION_LAYOUT_DEFAULT_APPEARANCE}
              tone="muted"
              icon={
                <div
                  className={cn(
                    "flex items-center justify-center overflow-hidden rounded-lg",
                    logo.url
                      ? "w-auto max-w-[min(22rem,30vw)] bg-transparent"
                      : "bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))]",
                  )}
                  style={
                    logo.url
                      ? { height: logoHeightPx }
                      : { width: logoHeightPx, height: logoHeightPx }
                  }
                >
                  {logo.url ? (
                    <img
                      src={logo.url}
                      alt={logo.title}
                      className="block h-full w-auto object-contain"
                    />
                  ) : (
                    <span className="text-base font-bold">
                      {logo.title.charAt(0)}
                    </span>
                  )}
                </div>
              }
              label={
                showBrandTitle ? (
                  <span className="truncate whitespace-nowrap text-base font-medium text-[hsl(var(--foreground))]">
                    {layout?.brandTitle}
                  </span>
                ) : null
              }
              ariaLabel={logo.title}
              title={logo.title}
              className="h-auto min-w-0 justify-start border-transparent bg-transparent p-0 shadow-none hover:bg-transparent"
              classNames={
                logo.url ? { icon: "!h-auto !w-auto shrink-0" } : undefined
              }
              onClick={logo.onClick}
            />
          ) : null)}
      </div>

      <div
        className={cn(
          "flex min-w-0 items-center",
          getHeadbarNavClass(layout?.navPosition),
        )}
      >
        <div className="no-scrollbar flex max-w-full min-w-0 items-center overflow-x-auto overscroll-x-contain">
          {navigation ?? (
            <NavigationHeadbarMenu
              activeItemId={activeItemId}
              activeRootId={activeRootId}
              items={navItems}
              menuRadius={menuRadius}
              onNavigate={onNavigate}
            />
          )}
        </div>
      </div>

      <div className="flex min-w-0 items-center justify-end gap-3">
        {actions}
        {extra}
        {accountMenu}
      </div>
    </header>
  );
}

export function NavigationLayout({
  activeItemId,
  activeRootId,
  allowModeSwitch = false,
  accountMenu,
  children,
  className,
  collapsedSidebarAccountMenu,
  collapsedSidebarWidth = NAVIGATION_LAYOUT_SIDEBAR_COLLAPSED_WIDTH,
  contextualSidebar = false,
  contextualSidebarHeader,
  contextualSidebarWidth = NAVIGATION_LAYOUT_CONTEXTUAL_SIDEBAR_WIDTH,
  defaultMode = "headbar",
  defaultSidebarCollapsed = false,
  expandedSidebarWidth = NAVIGATION_LAYOUT_SIDEBAR_EXPANDED_WIDTH,
  expandSidebarLabel = "Expand sidebar",
  flush = false,
  headbarActions,
  headbarLayout,
  logo,
  mode,
  navItems,
  onModeChange,
  onNavigate,
  onSidebarCollapsedChange,
  renderHeadbarExtra,
  collapseSidebarLabel = "Collapse sidebar",
  sidebarCollapsed: controlledSidebarCollapsed,
  sidebarAccountMenu,
  sidebarFooter,
  sidebarHeader,
  switchToHeadbarLabel = "Switch to headbar",
  switchToSidebarLabel = "Switch to sidebar",
}: NavigationLayoutProps) {
  const [uncontrolledMode, setUncontrolledMode] =
    useState<NavigationMode>(defaultMode);
  const [uncontrolledSidebarCollapsed, setUncontrolledSidebarCollapsed] =
    useState(defaultSidebarCollapsed);
  const [revealRequest, setRevealRequest] = useState<{
    id: string;
    nonce: number;
  } | null>(null);
  const isModeControlled = mode !== undefined;
  const isSidebarCollapsedControlled = controlledSidebarCollapsed !== undefined;
  const navigationMode = isModeControlled ? mode : uncontrolledMode;
  const sidebarCollapsed = isSidebarCollapsedControlled
    ? controlledSidebarCollapsed
    : uncontrolledSidebarCollapsed;
  const isHeadbarMode = navigationMode === "headbar";
  const sidebarWidth = sidebarCollapsed
    ? collapsedSidebarWidth
    : expandedSidebarWidth;
  const resolvedActiveRootId =
    activeRootId ?? findNavigationRootId(navItems, activeItemId);
  const activeRoot = navItems.find((item) => item.id === resolvedActiveRootId);
  const contextualItems = activeRoot?.children ?? [];

  useEffect(() => {
    if (!isModeControlled) {
      setUncontrolledMode(defaultMode);
    }
  }, [defaultMode, isModeControlled]);

  useEffect(() => {
    if (!isSidebarCollapsedControlled) {
      setUncontrolledSidebarCollapsed(defaultSidebarCollapsed);
    }
  }, [defaultSidebarCollapsed, isSidebarCollapsedControlled]);

  const setNavigationMode = (nextMode: NavigationMode) => {
    if (!isModeControlled) {
      setUncontrolledMode(nextMode);
    }
    onModeChange?.(nextMode);
  };

  const setCollapsed = (collapsed: boolean) => {
    if (!isSidebarCollapsedControlled) {
      setUncontrolledSidebarCollapsed(collapsed);
    }
    onSidebarCollapsedChange?.(collapsed);
  };

  const navigationActions: NavigationLayoutAction[] = [];
  if (allowModeSwitch) {
    navigationActions.push({
      id: "toggle-headbar",
      label: switchToHeadbarLabel,
      onClick: () => setNavigationMode("headbar"),
      icon: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="h-[18px] w-[18px] rotate-180"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <rect width="18" height="18" x="3" y="3" rx="2" />
          <path d="M3 9h18" />
        </svg>
      ),
    });
  }
  navigationActions.push({
    id: "toggle-sidebar",
    label: sidebarCollapsed ? expandSidebarLabel : collapseSidebarLabel,
    onClick: () => setCollapsed(!sidebarCollapsed),
    icon: (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        className="h-[18px] w-[18px]"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <rect width="18" height="18" x="3" y="3" rx="2" />
        <path d="M9 3v18" />
        <path d={sidebarCollapsed ? "m14 9 3 3-3 3" : "m16 15-3-3 3-3"} />
      </svg>
    ),
  });

  const sidebar = isHeadbarMode && contextualSidebar && contextualItems.length ? (
    <aside
      className="relative h-full min-h-0 shrink-0 overflow-hidden"
      data-navigation-contextual-sidebar={resolvedActiveRootId}
      style={{ width: contextualSidebarWidth }}
    >
      <div className="flex h-full min-h-0 w-full flex-col gap-3 rounded-xl border border-[hsl(var(--input))] bg-[hsl(var(--background))] px-4 py-4">
        <div className="shrink-0 px-2 text-sm font-semibold text-[hsl(var(--foreground))]">
          {contextualSidebarHeader ?? activeRoot?.label}
        </div>
        <SidebarNavigationList
          activeItemId={activeItemId}
          items={contextualItems}
          onNavigate={onNavigate}
        />
      </div>
    </aside>
  ) : isHeadbarMode ? null : (
    <div
      className={cn(
        "relative h-full min-h-0 shrink-0 transition-[width] duration-260",
        sidebarCollapsed ? "z-20 overflow-visible" : "z-10 overflow-hidden",
      )}
      style={{
        width: sidebarWidth,
        transitionTimingFunction: NAVIGATION_LAYOUT_TRANSITION_TIMING,
      }}
    >
      <Sidebar
        accountMenu={sidebarAccountMenu ?? accountMenu}
        activeItemId={activeItemId}
        actions={navigationActions}
        collapsed={sidebarCollapsed}
        collapsedAccountMenu={collapsedSidebarAccountMenu ?? accountMenu}
        footer={sidebarFooter}
        header={sidebarHeader}
        items={navItems}
        logo={logo}
        onCollapsedChange={setCollapsed}
        onNavigate={onNavigate}
        onRevealRoot={(rootId) =>
          setRevealRequest((prev) => ({
            id: rootId,
            nonce: (prev?.nonce ?? 0) + 1,
          }))
        }
        revealRequest={revealRequest}
        title={headbarLayout?.brandTitle}
      />
    </div>
  );

  const header = isHeadbarMode ? (
    <NavigationHeadbar
      activeItemId={activeItemId}
      activeRootId={resolvedActiveRootId}
      actions={
        allowModeSwitch ? (
          <>
            {headbarActions}
            <BaseButton
              appearance={NAVIGATION_LAYOUT_DEFAULT_APPEARANCE}
              tone="muted"
              icon={
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-4 w-4 shrink-0"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <rect width="18" height="18" x="3" y="3" rx="2" />
                  <path d="M9 3v18" />
                  <path d="m14 9 3 3-3 3" />
                </svg>
              }
              label={
                <span className="truncate whitespace-nowrap">
                  {switchToSidebarLabel}
                </span>
              }
              onClick={() => setNavigationMode("sidebar")}
              className={cn(
                "h-auto min-w-0 shrink px-3 py-2 text-sm font-medium text-[hsl(var(--muted-foreground))] shadow-none hover:border-[hsl(var(--input))]/70 hover:bg-[hsl(var(--muted))]/60 hover:text-[hsl(var(--foreground))]",
                getHeadbarMenuRadiusClass(headbarLayout?.menuRadius),
              )}
            />
          </>
        ) : (
          headbarActions
        )
      }
      accountMenu={accountMenu}
      extra={renderHeadbarExtra}
      layout={headbarLayout}
      logo={logo}
      navItems={navItems}
      onNavigate={onNavigate}
    />
  ) : null;

  return (
    <AppLayout
      header={header}
      sidebar={sidebar}
      flush={flush}
      className={className}
    >
      {children}
    </AppLayout>
  );
}
